import moment, { isMoment } from 'moment'

import { BACKEND_DATE_FORMAT } from '../constants'
import { getTimezoneLessMoment, toEuropeanDate } from '../momentjs'
import { ValidationFields } from '../validators'
import { getEntityOrNull, getIdOrNull } from '..'

export const IN_INSTITUTION_STATE = 'in-institution'

/** @enum {string} */
export const MaccsInternshipProperties = Object.freeze({
  SCHOOL: ValidationFields.SCHOOL,
  START_DATE: ValidationFields.START_DATE,
  END_DATE: ValidationFields.END_DATE,
  INSTITUTION: ValidationFields.INSTITUTION,
  ASSISTANT: ValidationFields.ASSISTANT,
  SPECIALITY: ValidationFields.SPECIALITY,
  WORKING_REGIME: ValidationFields.WORKING_REGIME
})

export const MaccsInternshipValidators = Object.freeze({
  [MaccsInternshipProperties.SCHOOL]: i => !!getEntityOrNull(i.school),
  [MaccsInternshipProperties.START_DATE]: i => isMoment(i.startDate),
  [MaccsInternshipProperties.END_DATE]: i => isMoment(i.endDate),
  [MaccsInternshipProperties.INSTITUTION]: i => !!getEntityOrNull(i.institution),
  [MaccsInternshipProperties.ASSISTANT]: i => !!getEntityOrNull(i.assistant),
  [MaccsInternshipProperties.SPECIALITY]: i => !!getEntityOrNull(i.speciality),
  [MaccsInternshipProperties.WORKING_REGIME]: i => !!getEntityOrNull(i.workingRegime)
})

export const makeMaccsInternship = ({ startDate, endDate, school, institution, internshipYear, assistant, workingRegime }) => {
  return {
    startDate: startDate ?? moment(),
    endDate: endDate ?? moment(),
    school: school ?? null,
    institution: institution ?? null,
    assistant: assistant ?? null,
    workingRegime: workingRegime ?? null,
    toRequestBody () {
      return {
        ...this,
        startDate: this.startDate.format(BACKEND_DATE_FORMAT),
        endDate: this.endDate.format(BACKEND_DATE_FORMAT)
      }
    }
  }
}

export const getSchoolName = mi => {
  if (!mi.school) {
    return 'Without school'
  }

  return mi.school.name
}

export const getSchoolId = mi => {
  if (!mi.school) {
    return null
  }

  return mi.school.id
}

export const getAssistantLastname = mi => {
  if (!mi.assistant) {
    return '/'
  }

  return mi.assistant.lastname
}

export const getAssistantFirstname = mi => {
  if (!mi.assistant) {
    return '/'
  }

  return mi.assistant.firstname
}

export const getAssistantEmail = mi => {
  if (!mi.assistant) {
    return '/'
  }

  return mi.assistant.email
}

export const getAssistantMatricule = mi => {
  if (!mi.assistant) {
    return '/'
  }

  return mi.assistant.matricule
}

export const getAssistantPhone = mi => {
  if (!mi.assistant) {
    return '/'
  }

  return mi.assistant.phone
}

export const getAssistantBirthdate = mi => {
  if (!mi.assistant) {
    return '/'
  }

  return toEuropeanDate(moment(mi.assistant.birthdate))
}

export const getStartDate = mi => {
  if (!mi.startDate) {
    return '/'
  }

  return toEuropeanDate(moment(mi.startDate.split('+')[0]))
}

export const getEndDate = mi => {
  if (!mi.endDate) {
    return '/'
  }

  return toEuropeanDate(moment(mi.endDate.split('+')[0]))
}

export const getSpeciality = mi => {
  if (!mi.speciality) {
    return 'Without speciality'
  }

  return mi.speciality.name
}

export const getInstitutionDescription = mi => {
  if (!mi.institution) {
    return 'Without institution'
  }

  return mi.institution.description ?? 'None'
}

export const getSpecialityAbbreviation = mi => {
  return mi.speciality?.abbreviation ?? '/'
}

export const getAssistantFullSpecialityAbbreviation = mi => {
  let abbreviation = '/'

  if (mi.assistant?.schoolSpeciality?.specialityYear) {
    const speciality = mi.assistant.schoolSpeciality.specialityYear

    abbreviation = `${speciality.specialityAbbreviation} (${speciality.studyYearName})`
  }

  return abbreviation
}

export const getAssistantFullFinancialYearAbbreviation = mi => {
  let abbreviation = '/'

  if (mi.assistant?.financialYear?.specialityAbbreviation) {
    abbreviation = `${mi.assistant.financialYear.specialityAbbreviation} (${mi.assistant.financialYear.studyYearName})`
  }

  return abbreviation
}

export const getWorkingRegimeName = mi => {
  return mi.workingRegimeName ?? '/'
}

export const getInstitutionName = mi => {
  let name = 'Without institution'

  if (mi.institution) {
    name = mi.institution.name
  }

  return name
}

export class MaccsInternship {
  constructor ({ id, startDate, endDate, school, institution, speciality, assistant, workingRegime }) {
    this.id = id ?? null
    this.startDate = getTimezoneLessMoment(startDate) ?? moment()
    this.endDate = getTimezoneLessMoment(endDate) ?? moment()
    this.school = school ?? null
    this.institution = institution ?? null
    this.speciality = speciality ?? null
    this.assistant = assistant ?? null
    this.workingRegime = workingRegime ?? null
  }

  toRequestBody () {
    const body = {
      ...this,
      startDate: this.startDate.format(BACKEND_DATE_FORMAT),
      endDate: this.endDate.format(BACKEND_DATE_FORMAT),
      school: getIdOrNull(this.school),
      institution: getIdOrNull(this.institution),
      speciality: getIdOrNull(this.speciality),
      assistant: getIdOrNull(this.assistant),
      workingRegime: getIdOrNull(this.workingRegime)
    }

    delete body.id

    return body
  }
}
